/**
 * Files to upload in FinanzOnline (Eingaben > Übermittlungen > Datenübermittlung): the UVA (form
 * U30) as XML, built from the same Kennzahlen as the draft on the reports page, and the
 * Zusammenfassende Meldung (form U13, `zmXml`) of intra-community supplies and services.
 *
 * The structure follows the BMF's schema "BMF_ERKLAERUNGS_UEBERMITTLUNG_U30_07_2026.xsd" (Stand
 * 24.04.2026, published for periods from 07/2026 with Kennzahlen 124 and 125) and its "Prüfungen"
 * list. What FinanzOnline would refuse is reported as a problem instead of written:
 *
 *  - amounts with a decimal point and two decimals; a Kennzahl of 0 is left out (only 000 and 070
 *    may carry 0), and none may be negative - a negative base is entered as 0 and corrected in
 *    067/090, which is a decision for a person;
 *  - the taxable bases must add up to 000 minus 021 minus the exempt ones, the acquisitions to 070;
 *  - the period must be over, and 124/125 exist only from July 2026;
 *  - 020 (other exempt turnover) needs the § 6 Abs 1 Ziffer it is exempt under (`VST`);
 *  - the output tax per rate and 095 are not transmitted: FinanzOnline computes them.
 *
 * The Steuernummer goes in as FASTNR: nine digits, the Finanzamt's two in front ("03 123/4567"
 * becomes 031234567). The same number identifies the sender, which is right for someone filing
 * their own UVA; a Kanzlei uploads under its own.
 */
import { isWithin, type Period } from "./periods";
import type { UvaCode, UvaDocument, UvaResult } from "./uva";

export type FilingProblem =
  | "tax_number"
  | "period_open"
  | "too_old"
  | "negative"
  | "rate_4_9"
  | "exempt_paragraph"
  | "sums"
  | "no_vat_id"
  | "nothing";

/** "03 123/4567" -> "031234567", or "" when it is not a nine-digit FinanzOnline number. */
export function fastnr(taxNumber: string | undefined): string {
  const digits = (taxNumber ?? "").replace(/\D/g, "");
  if (!/^\d{9}$/.test(digits)) return "";
  const value = Number(digits);
  return value >= 10000010 && value <= 989999999 ? digits : "";
}

/** "2026-07-01" -> "2026-07". */
const yearMonth = (date: string) => date.slice(0, 7);
const cents = (euros: number) => Math.round(euros * 100);
const kz = (code: string, euros: number) =>
  `<KZ${code} type="kz">${(cents(euros) / 100).toFixed(2)}</KZ${code}>`;

/** INFO_DATEN: who sends, when, and how many declarations follow. */
function header(sender: string, created: Date, count: number): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  const date = `${created.getFullYear()}-${pad(created.getMonth() + 1)}-${pad(created.getDate())}`;
  const time = `${pad(created.getHours())}:${pad(created.getMinutes())}:${pad(created.getSeconds())}`;
  // Any number the sender chooses; the time keeps two uploads apart.
  const packet = Math.floor(created.getTime() / 1000) % 999_999_999 || 1;
  return [
    "<INFO_DATEN>",
    "<ART_IDENTIFIKATIONSBEGRIFF>FASTNR</ART_IDENTIFIKATIONSBEGRIFF>",
    `<IDENTIFIKATIONSBEGRIFF>${sender}</IDENTIFIKATIONSBEGRIFF>`,
    `<PAKET_NR>${packet}</PAKET_NR>`,
    `<DATUM_ERSTELLUNG type="datum">${date}</DATUM_ERSTELLUNG>`,
    `<UHRZEIT_ERSTELLUNG type="uhrzeit">${time}</UHRZEIT_ERSTELLUNG>`,
    `<ANZAHL_ERKLAERUNGEN>${count}</ANZAHL_ERKLAERUNGEN>`,
    "</INFO_DATEN>",
  ].join("");
}

function general(form: "U30" | "U13", period: Period, number: string, info: string): string {
  return [
    "<ALLGEMEINE_DATEN>",
    `<ANBRINGEN>${form}</ANBRINGEN>`,
    `<ZRVON type="jahrmonat">${yearMonth(period.from)}</ZRVON>`,
    `<ZRBIS type="jahrmonat">${yearMonth(period.to)}</ZRBIS>`,
    `<FASTNR>${number}</FASTNR>`,
    `<KUNDENINFO>${info.replace(/[^A-Za-z0-9 ./-]/g, "").slice(0, 50)}</KUNDENINFO>`,
    "</ALLGEMEINE_DATEN>",
  ].join("");
}

/** Problems every upload shares: the number, and a period FinanzOnline accepts. */
function periodProblems(period: Period, taxNumber: string, today: string): FilingProblem[] {
  const problems: FilingProblem[] = [];
  if (!fastnr(taxNumber)) problems.push("tax_number");
  // ZRBIS must lie before the current month (or quarter), and at most five years back.
  if (period.to >= `${today.slice(0, 7)}-01`) problems.push("period_open");
  if (period.year < Number(today.slice(0, 4)) - 5) problems.push("too_old");
  return problems;
}

const wrap = (body: string, encoding: string) =>
  `<?xml version="1.0" encoding="${encoding}"?>\n<ERKLAERUNGS_UEBERMITTLUNG>${body}</ERKLAERUNGS_UEBERMITTLUNG>\n`;

/** The XSD's order inside each group; a Kennzahl outside these lists is never written. */
const U30_GROUPS = {
  free: ["011", "017", "016", "020"],
  taxed: ["022", "124", "029", "006", "037", "057"],
  acquired: ["072", "125", "073", "008", "088"],
  input: ["060", "061", "065", "066"],
} satisfies Record<string, UvaCode[]>;

export type U30Input = {
  uva: Pick<UvaResult, "amounts">;
  period: Period;
  taxNumber: string;
  /** The Ziffer of § 6 Abs 1 the turnover in 020 is exempt under, e.g. "19" for a doctor. */
  exemptParagraph?: string;
  created: Date;
  /** ISO day, to tell whether the period is over. */
  today: string;
};

export function u30Xml(input: U30Input): { xml: string; problems: FilingProblem[] } {
  const { amounts } = input.uva;
  const value = (code: UvaCode) => cents(amounts[code] ?? 0);
  const problems = periodProblems(input.period, input.taxNumber, input.today);

  const transmitted = ["000", "021", "070", ...Object.values(U30_GROUPS).flat()] as UvaCode[];
  if (transmitted.some((code) => value(code) < 0)) problems.push("negative");
  if (input.period.from < "2026-07-01" && (value("124") || value("125"))) problems.push("rate_4_9");
  const paragraph = (input.exemptParagraph ?? "").trim();
  if (value("020") && !/^[0-9][0-9a-zA-Z]{0,3}$/.test(paragraph)) problems.push("exempt_paragraph");

  const sum = (codes: UvaCode[]) => codes.reduce((total, code) => total + value(code), 0);
  const taxedBases = sum(["022", "124", "029", "006", "037"]);
  if (taxedBases !== value("000") - value("021") - sum(U30_GROUPS.free)) problems.push("sums");
  if (sum(U30_GROUPS.acquired) !== value("070")) problems.push("sums");

  const present = (codes: UvaCode[]) => codes.filter((code) => value(code) > 0);
  const group = (name: string, inner: string) => (inner ? `<${name}>${inner}</${name}>` : "");
  const free = present(U30_GROUPS.free)
    .map((code) =>
      code === "020"
        ? `<VST>${paragraph}</VST>${kz(code, amounts[code])}`
        : kz(code, amounts[code]),
    )
    .join("");
  const taxed = present(U30_GROUPS.taxed)
    .map((code) => kz(code, amounts[code]))
    .join("");
  const acquired = present(U30_GROUPS.acquired)
    .map((code) => kz(code, amounts[code]))
    .join("");
  const deductions = present(U30_GROUPS.input)
    .map((code) => kz(code, amounts[code]))
    .join("");

  const number = fastnr(input.taxNumber);
  const body = [
    header(number, input.created, 1),
    '<ERKLAERUNG art="U30">',
    "<SATZNR>1</SATZNR>",
    general("U30", input.period, number, `UVA ${input.period.key}`),
    "<LIEFERUNGEN_LEISTUNGEN_EIGENVERBRAUCH>",
    // 000 is the one Kennzahl always written, 0 included.
    `<KZ000 type="kz">${(Math.max(0, value("000")) / 100).toFixed(2)}</KZ000>`,
    value("021") > 0 ? kz("021", amounts["021"]) : "",
    group("STEUERFREI", free),
    group("VERSTEUERT", taxed),
    "</LIEFERUNGEN_LEISTUNGEN_EIGENVERBRAUCH>",
    value("070") > 0
      ? group(
          "INNERGEMEINSCHAFTLICHE_ERWERBE",
          kz("070", amounts["070"]) + group("VERSTEUERT_IGE", acquired),
        )
      : "",
    group("VORSTEUER", deductions),
    "</ERKLAERUNG>",
  ].join("");
  return { xml: wrap(body, "iso-8859-1"), problems };
}

export type ZmDocument = Pick<
  UvaDocument,
  "kind" | "status" | "taxTreatment" | "issueDate" | "netTotal"
> & { partnerVatId?: string };

export type ZmLine = {
  /** The customer's UID with its country prefix, e.g. DE123456789. */
  vatId: string;
  /** Whole euros, the sum of the period's net amounts to that customer. */
  amount: number;
  /** A service under § 3a Abs 6 (our reverse charge invoice), not a delivery. */
  service: boolean;
};

/**
 * The lines of the Zusammenfassende Meldung (form U13): per customer UID, the intra-community
 * supplies (`eu_intra_community`) and the services the customer owes the tax for
 * (`reverse_charge`), each kind on its own line. The month the invoice was issued decides, on a
 * cash basis too (Art 21 Abs 3 UStG), and the BMF's schema wants whole euros.
 */
export function zmLines(documents: ZmDocument[], period: Period): ZmLine[] {
  const sums = new Map<string, ZmLine & { cents: number }>();
  for (const document of documents) {
    if (document.kind !== "AR" || document.status !== "booked") continue;
    const service = document.taxTreatment === "reverse_charge";
    if (!service && document.taxTreatment !== "eu_intra_community") continue;
    if (!isWithin(document.issueDate, period)) continue;
    const vatId = (document.partnerVatId ?? "").replace(/\s+/g, "").toUpperCase();
    const key = `${vatId}|${service}`;
    const line = sums.get(key) ?? { vatId, amount: 0, service, cents: 0 };
    line.cents += cents(document.netTotal ?? 0);
    sums.set(key, line);
  }
  return (
    [...sums.values()]
      .map(({ cents: total, ...line }) => ({ ...line, amount: Math.round(total / 100) }))
      // A credit note that cancels the month's sales leaves nothing to report; a missing UID stays.
      .filter((line) => line.amount !== 0 || !line.vatId)
      .sort((a, b) => a.vatId.localeCompare(b.vatId) || Number(a.service) - Number(b.service))
  );
}

/** An EU UID other than Austria's: two letters (EL for Greece, XI for Northern Ireland) and 2-13 more. */
const foreignVatId = (value: string) =>
  /^[A-Z]{2}[0-9A-Z+*]{2,13}$/.test(value) && !value.startsWith("AT");

export type ZmInput = {
  lines: ZmLine[];
  period: Period;
  taxNumber: string;
  created: Date;
  today: string;
};

/** The ZM as FinanzOnline takes it ("BMF_XSD_Schema_Zusammenfassende_Meldung.xsd", 14.01.2025). */
export function zmXml(input: ZmInput): { xml: string; problems: FilingProblem[] } {
  const problems = periodProblems(input.period, input.taxNumber, input.today);
  if (input.lines.length === 0) problems.push("nothing");
  if (input.lines.some((line) => !foreignVatId(line.vatId))) problems.push("no_vat_id");

  const number = fastnr(input.taxNumber);
  const body = [
    header(number, input.created, 1),
    '<ERKLAERUNG art="U13">',
    "<SATZNR>1</SATZNR>",
    general("U13", input.period, number, `ZM ${input.period.key}`),
    ...input.lines.map(
      (line) =>
        `<ZM><UID_MS>${line.vatId}</UID_MS><SUM_BGL type="kz">${line.amount}</SUM_BGL>` +
        `${line.service ? "<SOLEI>J</SOLEI>" : ""}</ZM>`,
    ),
    "</ERKLAERUNG>",
  ].join("");
  return { xml: wrap(body, "UTF-8"), problems };
}
